import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Joyride,
  ACTIONS,
  EVENTS,
  ORIGIN,
  STATUS,
  type EventData,
  type Step,
} from 'react-joyride';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import {
  applyUserTourProgress,
  getTourProgress,
  isTourDone,
} from '../utils/userPreferences';
import { logTourEvent, persistTourStatus } from './api';
import ContinueGate from './ContinueGate';
import {
  chapterForPath,
  firstPendingChapter,
  flowKey,
  getChapterById,
  getChapterSequence,
  nextChapter,
  parseFlowMode,
  rolePrefix,
} from './chapters';
import TourTooltip from './TourTooltip';
import { TourUiBridgeProvider, useTourUiBridge } from './TourUiBridge';
import type { TourChapterDef, TourFlowMode } from './types';

type TourContextValue = {
  isSampleMode: boolean;
  activeChapterId: string | null;
  flowMode: TourFlowMode | null;
  startChapter: (chapterId: string, opts?: { replay?: boolean }) => void;
  replayIntro: () => void;
};

const TourContext = createContext<TourContextValue | null>(null);

function slideOverOpen(): boolean {
  return Boolean(
    document.querySelector('[role="dialog"]') ||
      document.querySelector('.fixed.inset-0.z-50') ||
      document.querySelector('[data-slide-over-open="true"]')
  );
}

function waitForTarget(selector: string, timeoutMs = 4000): Promise<boolean> {
  if (selector === 'body') return Promise.resolve(true);
  const start = Date.now();
  return new Promise((resolve) => {
    const tick = () => {
      if (document.querySelector(selector)) {
        resolve(true);
        return;
      }
      if (Date.now() - start > timeoutMs) {
        resolve(false);
        return;
      }
      requestAnimationFrame(tick);
    };
    tick();
  });
}

function TourEngine({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const { setOpenSlideOver, clearSlideOver } = useTourUiBridge();

  const [isSampleMode, setIsSampleMode] = useState(false);
  const [activeChapterId, setActiveChapterId] = useState<string | null>(null);
  const [flowMode, setFlowMode] = useState<TourFlowMode | null>(null);
  const [run, setRun] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  const [steps, setSteps] = useState<Step[]>([]);
  const [gateOpen, setGateOpen] = useState(false);
  const [gateChapterId, setGateChapterId] = useState<string | null>(null);

  const finishingRef = useRef(false);
  const autoStartedRef = useRef<string | null>(null);
  const hydratedRef = useRef(false);
  const chapterStepsRef = useRef<TourChapterDef['steps']>([]);
  const guidedSampleRef = useRef(false);

  const prefix = rolePrefix(user?.role);

  useEffect(() => {
    if (!user?.id || hydratedRef.current) return;
    hydratedRef.current = true;
    applyUserTourProgress(user as { id: number; tourProgress?: unknown });
    if (prefix) {
      setFlowMode(parseFlowMode(getTourProgress(user.id), prefix));
    }
  }, [user, prefix]);

  const showGate = useCallback((chapterId: string) => {
    setRun(false);
    clearSlideOver();
    setGateChapterId(chapterId);
    setGateOpen(true);
  }, [clearSlideOver]);

  const finishAll = useCallback(async () => {
    if (!prefix || !user?.id) return;
    guidedSampleRef.current = false;
    setIsSampleMode(false);
    setGateOpen(false);
    setActiveChapterId(null);
    setRun(false);
    clearSlideOver();
    setFlowMode('done');
    await persistTourStatus(flowKey(prefix), 'completed', { mode: 'done' });
  }, [clearSlideOver, prefix, user?.id]);

  const endChapter = useCallback(
    async (status: 'completed' | 'skipped', chapterId: string, lastStep: number) => {
      if (finishingRef.current) return;
      finishingRef.current = true;
      setRun(false);
      clearSlideOver();
      try {
        await persistTourStatus(chapterId, status, { lastStep });
        await logTourEvent({
          type: status === 'completed' ? 'TOUR_COMPLETED' : 'TOUR_SKIPPED',
          tourId: chapterId,
          stepIndex: lastStep,
          role: user?.role,
          platform: 'web',
        });
        showGate(chapterId);
      } catch {
        showGate(chapterId);
      } finally {
        finishingRef.current = false;
      }
    },
    [clearSlideOver, showGate, user?.role]
  );

  const prepareAndStart = useCallback(
    async (chapter: TourChapterDef, mode: TourFlowMode) => {
      setActiveChapterId(chapter.id);
      setGateOpen(false);
      setStepIndex(0);
      chapterStepsRef.current = chapter.steps;

      const sample =
        mode === 'guided' ? true : Boolean(chapter.useSampleData);
      if (mode === 'guided') guidedSampleRef.current = true;
      setIsSampleMode(sample || guidedSampleRef.current);

      if (prefix && mode === 'guided') {
        setFlowMode('guided');
        void persistTourStatus(flowKey(prefix), 'skipped', { mode: 'guided' });
      }

      const joySteps: Step[] = chapter.steps.map((s, i) => ({
        target: s.target,
        title: t(s.titleKey),
        content: t(s.bodyKey),
        placement: s.placement === 'center' ? 'center' : s.placement || 'auto',
        skipBeacon: true,
        blockTargetInteraction: true,
        data: { route: s.route, openSlideOver: s.openSlideOver, stepIndex: i },
        before: async () => {
          if (s.openSlideOver) {
            setOpenSlideOver(s.openSlideOver);
            await new Promise((r) => setTimeout(r, 220));
          } else {
            clearSlideOver();
          }
          if (s.route) {
            const current = `${location.pathname}${location.search}`;
            const routePath = s.route.split('?')[0];
            if (current !== s.route && location.pathname !== routePath) {
              navigate(s.route);
              await waitForTarget(s.target, 5000);
              await new Promise((r) => setTimeout(r, 150));
            }
          }
          await waitForTarget(s.target, 4000);
        },
        after: () => {
          clearSlideOver();
        },
      }));
      setSteps(joySteps);

      const firstRoute = chapter.route;
      if (firstRoute) {
        const routePath = firstRoute.split('?')[0];
        if (location.pathname !== routePath) {
          navigate(firstRoute);
        }
      }

      clearSlideOver();
      await waitForTarget(chapter.steps[0]?.target || 'body');
      await new Promise((r) => setTimeout(r, 160));
      setRun(true);
      void logTourEvent({
        type: 'TOUR_STARTED',
        tourId: chapter.id,
        stepIndex: 0,
        role: user?.role,
        platform: 'web',
      });
    },
    [
      clearSlideOver,
      location.pathname,
      location.search,
      navigate,
      prefix,
      setOpenSlideOver,
      t,
      user?.role,
    ]
  );

  const startChapter = useCallback(
    (chapterId: string, opts?: { replay?: boolean }) => {
      if (!user?.id || !prefix) return;
      if (!opts?.replay && isTourDone(chapterId, user.id)) return;
      const chapter = getChapterById(user.role, chapterId);
      if (!chapter) return;
      const mode = parseFlowMode(getTourProgress(user.id), prefix) || 'guided';
      void prepareAndStart(chapter, mode === 'done' ? 'guided' : mode);
    },
    [prepareAndStart, prefix, user?.id, user?.role]
  );

  const replayIntro = useCallback(async () => {
    if (!user?.id || !prefix) return;
    const seq = getChapterSequence(user.role);
    if (!seq.length) return;
    guidedSampleRef.current = true;
    setFlowMode('guided');
    await persistTourStatus(flowKey(prefix), 'skipped', {
      mode: 'guided',
      replace: true,
    });
    autoStartedRef.current = null;
    void prepareAndStart(seq[0], 'guided');
  }, [prepareAndStart, prefix, user?.id, user?.role]);

  // Auto-start guided / discover
  useEffect(() => {
    if (!user?.id || !prefix || user.impersonating) return;
    if (user.role === 'SUPER_ADMIN') return;
    // Firma Ompli session sandbox — never start product tour
    if (user.isDemo) return;
    if (run || activeChapterId || gateOpen) return;
    if (slideOverOpen()) return;

    const progress = getTourProgress(user.id);
    const mode = parseFlowMode(progress, prefix);

    if (mode === 'done') return;

    if (!mode || mode === 'guided') {
      const pending = firstPendingChapter(user.role, progress);
      if (!pending) {
        void finishAll();
        return;
      }
      // Only auto-kick guided from dashboard/first page (or when already on pending route)
      const onPending =
        location.pathname === pending.route.split('?')[0] ||
        location.pathname === '/dashboard';
      if (!onPending && mode === 'guided') return;
      const key = `guided:${pending.id}`;
      if (autoStartedRef.current === key) return;
      autoStartedRef.current = key;
      const timer = window.setTimeout(() => {
        void prepareAndStart(pending, 'guided');
      }, 700);
      return () => window.clearTimeout(timer);
    }

    if (mode === 'discover') {
      const ch = chapterForPath(user.role, location.pathname, location.search);
      if (!ch || isTourDone(ch.id, user.id)) return;
      const key = `discover:${ch.id}`;
      if (autoStartedRef.current === key) return;
      autoStartedRef.current = key;
      const timer = window.setTimeout(() => {
        void prepareAndStart(ch, 'discover');
      }, 500);
      return () => window.clearTimeout(timer);
    }
  }, [
    user?.id,
    user?.role,
    user?.impersonating,
    prefix,
    location.pathname,
    location.search,
    run,
    activeChapterId,
    gateOpen,
    prepareAndStart,
    finishAll,
  ]);

  const handleEvent = useCallback(
    async (data: EventData) => {
      const { action, index, status, type, origin } = data;
      if (!activeChapterId) return;
      const defSteps = chapterStepsRef.current;

      if (status === STATUS.FINISHED) {
        await endChapter('completed', activeChapterId, index);
        return;
      }
      if (status === STATUS.SKIPPED || action === ACTIONS.SKIP) {
        await endChapter('skipped', activeChapterId, index);
        return;
      }
      if (
        action === ACTIONS.CLOSE &&
        (origin === ORIGIN.KEYBOARD || origin === ORIGIN.OVERLAY || origin === ORIGIN.BUTTON_CLOSE)
      ) {
        await endChapter('skipped', activeChapterId, index);
        return;
      }

      if (type === EVENTS.STEP_AFTER || type === EVENTS.TARGET_NOT_FOUND) {
        const nextIndex = index + (action === ACTIONS.PREV ? -1 : 1);
        if (nextIndex < 0) return;
        if (nextIndex >= defSteps.length) {
          await endChapter('completed', activeChapterId, index);
          return;
        }
        setStepIndex(nextIndex);
        void logTourEvent({
          type: 'TOUR_STEP',
          tourId: activeChapterId,
          stepIndex: nextIndex,
          role: user?.role,
          platform: 'web',
        });
      }
    },
    [activeChapterId, endChapter, user?.role]
  );

  const gateChapter = gateChapterId ? getChapterById(user?.role, gateChapterId) : null;
  const gateNext = gateChapterId ? nextChapter(user?.role, gateChapterId) : null;

  const onGateContinue = useCallback(() => {
    if (!gateNext || !prefix) return;
    setGateOpen(false);
    void prepareAndStart(gateNext, 'guided');
  }, [gateNext, prefix, prepareAndStart]);

  const onGateDiscover = useCallback(async () => {
    if (!prefix) return;
    guidedSampleRef.current = false;
    setIsSampleMode(false);
    setGateOpen(false);
    setActiveChapterId(null);
    setFlowMode('discover');
    await persistTourStatus(flowKey(prefix), 'skipped', { mode: 'discover' });
  }, [prefix]);

  const value = useMemo(
    () => ({
      isSampleMode,
      activeChapterId,
      flowMode,
      startChapter,
      replayIntro,
    }),
    [isSampleMode, activeChapterId, flowMode, startChapter, replayIntro]
  );

  return (
    <TourContext.Provider value={value}>
      {children}
      <Joyride
        steps={steps}
        run={run}
        stepIndex={stepIndex}
        continuous
        scrollToFirstStep
        onEvent={handleEvent}
        tooltipComponent={TourTooltip}
        options={{
          zIndex: 9000,
          arrowColor: '#fff',
          backgroundColor: '#fff',
          overlayColor: 'rgba(15, 23, 42, 0.45)',
          primaryColor: '#0065F4',
          textColor: '#111827',
          skipBeacon: true,
          buttons: ['back', 'skip', 'primary'],
          closeButtonAction: 'skip',
          overlayClickAction: 'close',
          dismissKeyAction: 'close',
          showProgress: true,
          targetWaitTimeout: 5000,
        }}
        locale={{
          back: t('tour.back'),
          skip: t('tour.skip'),
          next: t('tour.next'),
          last: t('tour.next'),
          nextWithProgress: t('tour.next.progress'),
        }}
      />
      <ContinueGate
        open={gateOpen}
        isLast={!gateNext}
        nextTitle={gateNext ? t(gateNext.titleKey) : ''}
        onContinue={onGateContinue}
        onDiscover={onGateDiscover}
        onFinish={() => void finishAll()}
      />
    </TourContext.Provider>
  );
}

export const TourProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <TourUiBridgeProvider>
    <TourEngine>{children}</TourEngine>
  </TourUiBridgeProvider>
);

export function useTour(): TourContextValue {
  const ctx = useContext(TourContext);
  if (!ctx) {
    return {
      isSampleMode: false,
      activeChapterId: null,
      flowMode: null,
      startChapter: () => {},
      replayIntro: () => {},
    };
  }
  return ctx;
}
